package com.appcreator.compose.components.data

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.pulltorefresh.rememberPullToRefreshState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.ProvidedValue
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.paging.LoadState
import androidx.paging.Pager
import androidx.paging.PagingConfig
import androidx.paging.PagingConfig.Companion.MAX_SIZE_UNBOUNDED
import androidx.paging.PagingSource
import androidx.paging.PagingSource.LoadResult.Page.Companion.COUNT_UNDEFINED
import androidx.paging.PagingState
import androidx.paging.cachedIn
import androidx.paging.compose.LazyPagingItems
import androidx.paging.compose.collectAsLazyPagingItems
import androidx.paging.compose.itemKey
import com.appcreator.blueprint.Blueprint
import com.appcreator.blueprint.components.data.PagedLoaderComponent
import com.appcreator.blueprint.core.Condition
import com.appcreator.blueprint.core.EnvStore
import com.appcreator.blueprint.core.LoaderConfig
import com.appcreator.blueprint.core.LoaderSpec
import com.appcreator.blueprint.core.ReadonlyEnvStore
import com.appcreator.compose.AbsoluteSize
import com.appcreator.compose.LocalAbsoluteSize
import com.appcreator.compose.LocalBlueprint
import com.appcreator.compose.LocalEnvStore
import com.appcreator.compose.LocalInPreview
import com.appcreator.compose.LocalTriggerBus
import com.appcreator.compose.PlatformPullToRefreshBox
import com.appcreator.compose.components.ComponentComposable
import com.appcreator.compose.conditions.Evaluator
import com.appcreator.compose.di.Container
import com.appcreator.compose.di.evaluator
import com.appcreator.compose.loaders.Loader
import com.appcreator.compose.loaders.createPreviewData
import kotlinx.coroutines.delay


private class DataSource(
    private val inPreview: Boolean,
    private val previewDisplay: PagedLoaderComponent.PreviewState?,
    var envStore: EnvStore,
    private val config: LoaderConfig?,
    loadMore: Condition?,
    filter: Condition?,
    private val path: String?,
    blueprint: Blueprint
): PagingSource<EnvStore, EnvStore>() {

    companion object {
        const val indexTracker = "pagedLoaderLastIndex"
    }

    private val spec: LoaderSpec? = config?.let { blueprint.loaderSpec(it.loaderSpec) }
    private val pageLoader: Loader? = spec?.let { Container.loaderRegistry[it::class]?.invoke(it) }
    private val evaluator: Evaluator? = loadMore?.let { Container.evaluator(it) }
    private val filterEvaluator = filter?.let { Container.evaluator(it) }

    override suspend fun load(
        params: LoadParams<EnvStore>
    ): LoadResult<EnvStore, EnvStore> {
        return try {
            val storeToLoadFrom = params.key ?: envStore
            val data:  Pair<ProvidedValue<*>?, Map<String, Any>> = if(inPreview) {
                when(previewDisplay) {
                    PagedLoaderComponent.PreviewState.Loading -> {
                        while (true) { delay(300) }
                        throw IllegalStateException()
                    }
                    PagedLoaderComponent.PreviewState.Error -> throw IllegalStateException()
                    PagedLoaderComponent.PreviewState.Empty -> null to emptyMap()
                    else -> null to (spec?.createPreviewData()?: emptyMap())
                }
            } else pageLoader!!.load(storeToLoadFrom, config!!.parameters, null)

            val previousIndex = (storeToLoadFrom.get(indexTracker) as? Int) ?: 0
            val listItems = ReadonlyEnvStore.create(parent = envStore, env = data.second)
                .envFromList(path ?: "", indexOffset = previousIndex)
                .filter { filterEvaluator == null || filterEvaluator.evaluateAsync(it) }
            val envStore = ReadonlyEnvStore.create(parent = envStore, env = data.second.plus(indexTracker to (listItems.size + previousIndex)))

            val nextKey = when {
                inPreview -> null
                spec?.supportsPaging == true -> if (pageLoader?.canLoadMore(data.second) == true) envStore else null
                else -> if(evaluator!!.evaluateAsync(envStore)) envStore else null
            }

            LoadResult.Page(
                data = listItems,
                prevKey = null, // Only paging forward.
                nextKey = nextKey
            )
        } catch (ex: Throwable) {
            ex.printStackTrace()
            LoadResult.Error(ex)
        }
    }

    override fun getRefreshKey(state: PagingState<EnvStore, EnvStore>): EnvStore? {
        return null
    }


}

class PagerViewMode(
    var envStore: EnvStore,
    inPreview: Boolean,
    component: PagedLoaderComponent,
    blueprint: Blueprint
): ViewModel() {

    val pager = Pager(
        config =
            PagingConfig(
                pageSize = 20,
                prefetchDistance = 20,
                enablePlaceholders = false,
                initialLoadSize = 20,
                maxSize = MAX_SIZE_UNBOUNDED,
                jumpThreshold = COUNT_UNDEFINED
            ), initialKey = null
    ) {
        DataSource(
            inPreview = inPreview,
            previewDisplay = component.previewDisplay,
            envStore = envStore,
            config = component.config,
            loadMore = component.condition,
            filter = component.filter,
            path = component.itemsPath?.value,
            blueprint = blueprint
        )
    }.flow.cachedIn(viewModelScope)

}

@Composable
fun PagedLoaderComposable(modifier: Modifier, component: PagedLoaderComponent) {

    val envStore = LocalEnvStore.current
    val blueprint = LocalBlueprint.current
    val inPreview = LocalInPreview.current
    val key = component.config?.parameters?.map {
        envStore.injectVariables(it.value, staticPass = true)
    }
    val customKey = component.customKey?.let {
        envStore.injectVariables(it, staticPass = true)
    }?: ""

    var reloadInt by remember { mutableIntStateOf(0) }
    val viewModel = viewModel(key = customKey + key?.joinToString() + reloadInt.toString()) { PagerViewMode(envStore, inPreview, component, blueprint) }
    viewModel.envStore = envStore
    val pager = viewModel.pager
    val items = pager.collectAsLazyPagingItems()
    val refreshing = items.loadState.refresh == LoadState.Loading
    val pullToRefresh = rememberPullToRefreshState()
    val onRefresh: () -> Unit = {
        items.refresh()
    }

    component.reloadTrigger?.let {
        println("Paged loader a $it")
        val trigger = envStore.injectVariables(it)
        val bus = LocalTriggerBus.current
        DisposableEffect(trigger) {
            bus.addListener(trigger, onRefresh)
            onDispose {
                bus.removeListener(trigger)
            }
        }
    }

    val pullToRefreshEnabled = component.pullToRefresh == true

    val content: @Composable () -> Unit = {
        Box(Modifier.fillMaxSize()) {
            when(items.loadState.refresh) {
                LoadState.Loading -> Loading(modifier, component, pullToRefreshEnabled)
                is LoadState.Error -> ErrorView(modifier, component, (items.loadState.refresh as LoadState.Error).error) {
                    onRefresh()
                }
                else -> {
                    LoadedContent(modifier, component, items)
                }
            }
        }
    }
    if(pullToRefreshEnabled) {
        PlatformPullToRefreshBox(isRefreshing = refreshing, state = pullToRefresh, onRefresh = onRefresh) {
            content()
        }
    } else {
        content()
    }
}

@Composable
private fun Loading(modifier: Modifier, component: PagedLoaderComponent, pullToRefreshEnabled: Boolean) {
    when(component.loadingView) {
        PagedLoaderComponent.ViewType.Nothing -> {}
        PagedLoaderComponent.ViewType.Custom -> component.loadingContent?.let {
            ComponentComposable(
                modifier,
                it
            )
        }
        else -> {
            if (!pullToRefreshEnabled) {
                DefaultLoadingComposable(modifier)
            }
        }
    }
}

@Composable
private fun ErrorView(
    modifier: Modifier,
    component: PagedLoaderComponent,
    error: Throwable,
    refresh: () -> Unit
) {
    when(component.errorView) {
        PagedLoaderComponent.ViewType.Nothing -> {}
        PagedLoaderComponent.ViewType.Custom -> component.errorContent?.let { ComponentComposable(modifier, it) }
        else -> DefaultErrorComposable(modifier, error, refresh)
    }
}

@Composable
private fun LoadedContent(
    modifier: Modifier,
    component: PagedLoaderComponent,
    items: LazyPagingItems<EnvStore>,
) {
    val spacing = (component.itemSpacing?: 0).dp
    if(items.itemCount == 0) {
        Column(Modifier.verticalScroll(rememberScrollState())) {
            component.header?.let {
                if (component.showHeaderWithoutResults == true) {
                    Spacer(Modifier.height(spacing))
                    ComponentComposable(Modifier, it)
                    Spacer(Modifier.height(spacing))
                }
            }
            component.empty?.let {
                ComponentComposable(Modifier, it)
            }
        }
    } else {
        val envStore = LocalEnvStore.current
        LazyVerticalGrid(
            modifier = modifier.fillMaxWidth(),
            contentPadding = PaddingValues(vertical = spacing),
            columns = component.itemWidth?.let {
                if(it > 0) {
                    GridCells.FixedSize(it.dp)
                } else GridCells.Fixed(1)
            }?: GridCells.Fixed(1),
            verticalArrangement = Arrangement.spacedBy(spacing),
            horizontalArrangement = Arrangement.spacedBy(spacing, Alignment.CenterHorizontally)
        ) {

            component.header?.let {
                item(span = { GridItemSpan(maxCurrentLineSpan) }) {
                    BoxWithConstraints {
                        CompositionLocalProvider(LocalAbsoluteSize provides AbsoluteSize(this)) {
                            ComponentComposable(Modifier, it)
                        }
                    }
                }
            }

            items(items.itemCount, key = items.itemKey(key = { it.env })) {
                items[it]?.let { item ->
                    item.parent = envStore
                    CompositionLocalProvider(LocalEnvStore provides item) {
                        component.content?.let {
                            ComponentComposable(Modifier, it)
                        }
                    }
                }
            }

            when (items.loadState.append) {
                is LoadState.Loading -> item(span = { GridItemSpan(maxCurrentLineSpan) }) {
                    Box(
                        Modifier.fillMaxWidth().padding(8.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }

                else -> {
                    component.footer?.let {
                        item(span = { GridItemSpan(maxCurrentLineSpan) }) {
                            BoxWithConstraints {
                                CompositionLocalProvider(LocalAbsoluteSize provides AbsoluteSize(this)) {
                                    ComponentComposable(Modifier, it)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}